import { ErrorCodes, ErrorResponse, errorCodesFromJSON } from '../../proto/generated/common'

export interface ParsedError {
    errorCode: ErrorCodes | null
    message: string | null
    retryAfterSeconds?: number
}

const stringToErrorCode = Object.fromEntries(
    Object.entries(ErrorCodes)
        .filter(([key, value]) => typeof value === 'number')
        .map(([key, value]) => [key.toUpperCase(), value as ErrorCodes])
) as Record<string, ErrorCodes>

function mapStringToErrorCode(code: string | undefined): ErrorCodes | null {
    if (!code) return null
    return stringToErrorCode[code.toUpperCase()] ?? null
}

function toArrayBuffer(data: unknown): ArrayBuffer | null {
    if (data instanceof ArrayBuffer) {
        return data
    }
    if (data instanceof Uint8Array) {
        const sliced = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)
        // `buffer` may be a SharedArrayBuffer; copy into a plain ArrayBuffer in that case.
        return sliced instanceof ArrayBuffer ? sliced : new Uint8Array(sliced).slice().buffer
    }
    return null
}

function getErrorBuffer(err: any): ArrayBuffer | null {
    return toArrayBuffer(err?.response?.data) ?? toArrayBuffer(err?.data)
}

function isAxiosStatusFallbackMessage(message: string | null | undefined): boolean {
    return !!message && /^Request failed with status code \d+$/i.test(message.trim())
}

function parseJsonObjectError(data: unknown): ParsedError | null {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return null
    }
    const obj = data as Record<string, unknown>
    const rawCode = obj.errorCode ?? obj.error_code
    const rawMessage = obj.message
    const retry = Number(obj.retryAfterSeconds ?? obj.retry_after_seconds)
    const errorCode = mapStringToErrorCode(typeof rawCode === 'string' ? rawCode : undefined)
    const message = typeof rawMessage === 'string' && rawMessage.trim() ? rawMessage.trim() : null
    if (!errorCode && !message) {
        return null
    }
    return {
        errorCode,
        message,
        retryAfterSeconds: Number.isFinite(retry) && retry > 0 ? retry : undefined,
    }
}

function parseProtobufError(buffer: ArrayBuffer): ParsedError | null {
    try {
        const decoded = ErrorResponse.decode(new Uint8Array(buffer))
        if (!decoded.errorCode && !decoded.message) {
            return null
        }
        return {
            errorCode: decoded.errorCode ? errorCodesFromJSON(decoded.errorCode) : null,
            message: decoded.message || null,
            retryAfterSeconds: decoded.retryAfterSeconds || undefined,
        }
    } catch {
        return null
    }
}

function parseTextError(buffer: ArrayBuffer): ParsedError {
    let errorCode: ErrorCodes | null = null
    let message: string | null = null
    let retryAfterSeconds: number | undefined

    try {
        const text = new TextDecoder('utf-8').decode(new Uint8Array(buffer))
        try {
            const json = JSON.parse(text)
            const parsed = parseJsonObjectError(json)
            if (parsed) {
                return parsed
            }
            errorCode = mapStringToErrorCode(json.errorCode)
            message = json.message ?? null
            const retry = Number(json.retryAfterSeconds ?? json.retry_after_seconds)
            if (Number.isFinite(retry) && retry > 0) {
                retryAfterSeconds = retry
            }
        } catch {
            for (const codeKey in stringToErrorCode) {
                if (text.toUpperCase().includes(codeKey)) {
                    errorCode = stringToErrorCode[codeKey]
                    break
                }
            }
            message = text.trim() || null
        }
    } catch (decodeErr) {
        console.error('Failed to decode error response:', decodeErr)
    }

    return { errorCode, message, retryAfterSeconds }
}

/** Parses API errors from axios errors or adapter-transformed rejections (which use `data`, not `response.data`). */
export function parseErrorFromArrayBuffer(err: any): ParsedError {
    // responseType: 'json' yields an already-parsed object (e.g. send-email-otp 429).
    const jsonParsed =
        parseJsonObjectError(err?.response?.data) ?? parseJsonObjectError(err?.data)
    if (jsonParsed) {
        return jsonParsed
    }

    const buffer = getErrorBuffer(err)
    if (!buffer) {
        const fallback = typeof err?.message === 'string' ? err.message : null
        return {
            errorCode: null,
            message: isAxiosStatusFallbackMessage(fallback) ? null : fallback,
        }
    }

    return parseProtobufError(buffer) ?? parseTextError(buffer)
}

export const isNoDematAccountError = (err: any): boolean => {
    const { errorCode, message } = parseErrorFromArrayBuffer(err)
    if (errorCode === ErrorCodes.ENTITY_NOT_FOUND) {
        return true
    }
    return !!message?.toLowerCase().includes('no demat account')
}
